/**
 * Single source of truth for MaJourneys customer-facing contact details.
 * Apps must import these constants instead of hardcoding the email or phone
 * — keeps the brand consistent and lets us update everything from one place.
 */
export const CONTACT_EMAIL = "contact@majourneys.ma";

/**
 * One phone number reaches our team, and it is reachable two ways: as a WhatsApp
 * chat and as an ordinary call. Those are not the same offer.
 *
 * Stays deliberately offer only the call. We hold no line to the property, so a
 * chat thread there looks like property support and cannot answer a question
 * about a room or a key. Activities offer both, because we do mediate with a
 * local provider we can actually ring.
 *
 * So the number is named for what it is — the support phone — and the
 * WHATSAPP_NUMBER_* aliases below are kept for the activity surfaces that build
 * wa.me links from it. A `tel:` href should import SUPPORT_PHONE_*, otherwise
 * the import alone reads as a WhatsApp offer the surface is not making.
 */

/** International format, no spaces or dashes — drop into wa.me links and tel: hrefs. */
export const SUPPORT_PHONE_E164 = "+212663629134";

/** Display format with the company's preferred separators — for visible UI copy. */
export const SUPPORT_PHONE_DISPLAY = "+212 663-629134";

/** @deprecated Same number as {@link SUPPORT_PHONE_E164}; use that for `tel:` hrefs. */
export const WHATSAPP_NUMBER_E164 = SUPPORT_PHONE_E164;

/** @deprecated Same number as {@link SUPPORT_PHONE_DISPLAY}; use that outside WhatsApp UI. */
export const WHATSAPP_NUMBER_DISPLAY = SUPPORT_PHONE_DISPLAY;

/** mailto: href ready to use. */
export const CONTACT_EMAIL_MAILTO = `mailto:${CONTACT_EMAIL}` as const;

/** wa.me URL with no message preloaded. */
export const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER_E164.replace(/^\+/, "")}` as const;

/**
 * Support hours: when our team answers WhatsApp, every day, on Morocco's
 * clock. The one source for every surface that states or checks them (contact
 * page, need-help cards, account popup, the app's account hub).
 *
 * Until 2026-11-01 the contact page said 9:00 to 22:00 while the activity and
 * restaurant cards said 10:00 to 20:00 and "back at 10:00". The contact-page
 * hours were kept; confirm with the owner before changing them here.
 */
export const SUPPORT_OPENS_HOUR = 9;
export const SUPPORT_CLOSES_HOUR = 22;
export const SUPPORT_TIME_ZONE = "Africa/Casablanca";

/** Whether `now` falls inside support hours on the Casablanca clock. */
export function isSupportOnline(now: Date, timeZone: string = SUPPORT_TIME_ZONE): boolean {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", { hour: "2-digit", hourCycle: "h23", timeZone }).format(now),
  );
  return hour >= SUPPORT_OPENS_HOUR && hour < SUPPORT_CLOSES_HOUR;
}

/** An hour as the copy prints it: "9h00" in French, "9:00" everywhere else. */
export function formatSupportHour(hour: number, lang?: string | null): string {
  return (lang ?? "").startsWith("fr") ? `${hour}h00` : `${hour}:00`;
}

/** `{ open, close }` for the `{{open}}` / `{{close}}` placeholders in the copy. */
export function supportHoursVars(lang?: string | null): { open: string; close: string } {
  return { open: formatSupportHour(SUPPORT_OPENS_HOUR, lang), close: formatSupportHour(SUPPORT_CLOSES_HOUR, lang) };
}
